import { Box, Grid, InputAdornment } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLPaper } from "OLPaper";
import { useBasicSettingsData } from "api/basic-settings";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useSettings } from "api/general";
import { FormButtons } from "components/FormControls/FormButtons";
import { TextInput } from "components/FormControls/TextInput";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    formContainer: {
      width: "872px",
      margin: `${theme.spacing(8)} auto 0`,
      maxWidth: "100%",
    },
    OLPaper: {
      display: "flex",
      flexDirection: "column",
    },
    itemForm: {
      "& + &": {
        marginTop: theme.spacing(12),
      },
    },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      marginTop: theme.spacing(5),
      "& + &": {
        marginTop: 0,
      },
    },
    grayText: {
      color: theme.gray800,
      display: "block",
      fontSize: 18,
      fontWeight: 400,
      lineHeight: "21px",
    },
    graySubHeader: {
      color: theme.gray400,
      display: "block",
      fontSize: 16,
      lineHeight: "19px",
    },
    marginTop: {
      marginTop: theme.spacing(8),
    },
  }),
);

export function Communication() {
  const classes = useStyles();
  const { notify } = useNotifications();
  const { message } = useMessage();
  const settingsData = useBasicSettingsData("communication");
  const { updateSettings } = useSettings();
  const dataTestId = "expiring-notifications-form";
  const navigate = useNavigate();

  return (
    <>
      <TopHeader
        clearLink={links.webhookList}
        title={message({ id: "expiringNotifications" })}
      />
      <span className={`${classes.grayText} ${classes.marginTop}`}>
        {message({ id: "expiringUnitsConfiguration" })}
      </span>
      <div className={classes.formContainer}>
        {settingsData.formValues ? (
          <>
            <Formik
              enableReinitialize
              initialStatus={{}}
              initialValues={{
                expirePointsNotificationDays:
                  settingsData.formValues.expirePointsNotificationDays,
                expireCouponsNotificationDays:
                  settingsData.formValues.expireCouponsNotificationDays,
                expireLevelsNotificationDays:
                  settingsData.formValues.expireLevelsNotificationDays,
              }}
              onSubmit={async (values, { setSubmitting, setStatus }) => {
                setSubmitting(true);
                setStatus({});
                try {
                  const response = await updateSettings({
                    ...values,
                  });
                  if (response?.data?.settings) {
                    notify(message({ id: "settingsSaved" }));
                  }
                } catch (exception) {
                  setSubmitting(false);
                  notify(getErrorMessage(exception));
                  const errors = backendErrors(getBackendErrors(exception));
                  setStatus(errors);
                  focusError();
                }
              }}
            >
              {({
                handleSubmit,
                isSubmitting,
                status,
                dirty,
                values,
                initialValues,
              }) => {
                return (
                  <Form onSubmit={handleSubmit}>
                    {status?.[""] && (
                      <span className={classes.error}>{status[""]}</span>
                    )}
                    <Grid container direction="column" mt={8}>
                      <OLPaper className={classes.OLPaper}>
                        <span className={classes.grayText}>
                          {message({ id: "notificationsSettings" })}
                        </span>
                        <Box mt={4} />
                        <span className={classes.graySubHeader}>
                          {message({
                            id: "communicationSettingsInfo",
                          })}
                        </span>
                        <Box mt={12} />
                        <TextInput
                          className={classes.itemForm}
                          data-test-id={`${dataTestId}-expiring-units-notification-input`}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {message({ id: "days" })}
                              </InputAdornment>
                            ),
                          }}
                          label={message({
                            id: "expiringUnitsNotification",
                          })}
                          name="expirePointsNotificationDays"
                          type="number"
                        />
                        <TextInput
                          className={classes.itemForm}
                          data-test-id={`${dataTestId}-expiring-coupons-notification-input`}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {message({ id: "days" })}
                              </InputAdornment>
                            ),
                          }}
                          label={message({
                            id: "expiringCouponsNotification",
                          })}
                          name="expireCouponsNotificationDays"
                          type="number"
                        />
                        <TextInput
                          className={classes.itemForm}
                          data-test-id={`${dataTestId}-tier-recalculation-notification-input`}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {message({ id: "days" })}
                              </InputAdornment>
                            ),
                          }}
                          label={message({
                            id: "tierRecalculationNotification",
                          })}
                          name="expireLevelsNotificationDays"
                          type="number"
                        />
                      </OLPaper>
                      {!isSubmitting && (
                        <LeaveSiteGuard
                          warnBeforeRefresh
                          isDirty={dirty}
                          initialValues={initialValues}
                          currentValues={values}
                        />
                      )}
                      <Box mt={12} />
                      <FormButtons
                        disableSubmit={isSubmitting}
                        onCancelClick={() => navigate(links.webhookList)}
                        onSubmitClick={() => handleSubmit()}
                      />
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          </>
        ) : (
          <>
            <Skeleton height={300} width={"100%"} />
            <Box mt={12} />
            <FormButtons
              disableSubmit
              onCancelClick={() => {}}
              onSubmitClick={() => {}}
            />
          </>
        )}
      </div>
    </>
  );
}
